import React, {
  ReactElement,
  useState,
  useEffect,
  useMemo,
  useContext
} from 'react';
import {skipToken} from '@reduxjs/toolkit/query/react';
import Container from '@mui/material/Container';
import {Divider, Typography} from '@customink/pigment-react';
import {MobileContext} from '../../containers/MobileProvider';
import StoreProductDetailSidebar from '../StoreProductDetailSidebar';
import ProductList from './ProductList';
import {useAppSelector, useStoreType} from '../../hooks';
import {useGetStoreProductsQuery} from '../../api/storesApi';
import {useFetchProductsQuery} from '../../api/productsApi';
import './index.scss';
import {StoreProduct, StoreProductCollection} from '../../models/storeProduct';

const DEFAULT_CATEGORY = 'All';

type StoreProductsListingProps = {
  storeSlug: string;
};

export type StoreProductWithImage = StoreProduct & {
  mainImage: string;
};

const setCategoryStyle = (category: string, currentCategoryState: string) =>
  category === currentCategoryState;

const StoreProductsListing: React.FC<StoreProductsListingProps> = ({
  storeSlug
}: StoreProductsListingProps): ReactElement => {
  const {data: storeProductResults} = useGetStoreProductsQuery(storeSlug);
  useFetchProductsQuery(
    storeProductResults
      ? {ids: storeProductResults.map(p => p.styleId)}
      : skipToken
  );

  const mobile = useContext(MobileContext);

  const {isStoreTypeGroupOrder} = useStoreType();

  const underThreeProducts = storeProductResults?.length < 3;

  const mmsProductResults = useAppSelector(state => state.stores.mmsProducts);

  const [storeProductResultData, setStoreProductResultData] = useState<
    StoreProductWithImage[]
  >([]);
  const [storeProductsState, setStoreProductsState] = useState<
    StoreProductWithImage[]
  >([]);

  const [currentCategory, setCurrentCategory] = useState(DEFAULT_CATEGORY);

  useEffect(() => {
    if (storeProductResults) {
      const productList = storeProductResults.map(item => {
        return {...item, mainImage: item.imageUrl};
      });
      setStoreProductResultData(productList);
      setStoreProductsState(productList);
    }
  }, [storeProductResults]);

  const getCategories = (data: StoreProduct[]) => {
    const categories = [DEFAULT_CATEGORY];
    const addToList = (collections: StoreProductCollection[]) => {
      collections.forEach(collection => {
        if (!categories.includes(collection.name)) {
          categories.push(collection.name);
        }
      });
    };
    if (data) {
      data.forEach(storeProduct => {
        if (storeProduct.productCollections?.length > 0) {
          addToList(storeProduct.productCollections);
        }
      });
    }
    return categories;
  };

  const handleSelect = (
    selectedCategory: string,
    storeProductList: StoreProductWithImage[]
  ) => {
    if (selectedCategory === DEFAULT_CATEGORY) {
      setCurrentCategory(DEFAULT_CATEGORY);
      return setStoreProductsState(storeProductResultData);
    }

    setCurrentCategory(selectedCategory);
    const foundProducts = storeProductList.filter(
      x =>
        x.productCollections?.length > 0 &&
        x.productCollections.some(y => y.name === selectedCategory)
    );
    return setStoreProductsState(foundProducts);
  };

  const showCategories = (): boolean => {
    const categories = getCategories(storeProductResults);
    return !(categories.length === 1 && categories[0] === DEFAULT_CATEGORY);
  };

  const categoriesList = useMemo(() => {
    if (storeProductResults && showCategories()) {
      return (
        <ul>
          {getCategories(storeProductResults).map(item => {
            return (
              <li key={item}>
                <Typography
                  className={`category${
                    setCategoryStyle(item, currentCategory) ? '-selected' : ''
                  }`}
                  variant="bodyLong2"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelect(item, storeProductResultData)}
                  onKeyDown={() => handleSelect(item, storeProductResultData)}>
                  {item}
                </Typography>
              </li>
            );
          })}
        </ul>
      );
    }
    return [];
  }, [storeProductResults, storeProductsState]);

  return (
    <div className="storeProductListing">
      <Container className="storeProductListing-container">
        <div className="categories">
          {storeProductResults && categoriesList}
        </div>
        <div className={`storeProductGrid${mobile ? ' mobile' : ''}`}>
          {isStoreTypeGroupOrder ? (
            <>
              <StoreProductDetailSidebar storeSlug={storeSlug} />
              <div className="divider-wrapper">
                <Divider
                  orientation={mobile ? 'horizontal' : 'vertical'}
                  className="divider"
                />
              </div>
            </>
          ) : null}
          <ProductList
            storeSlug={storeSlug}
            products={storeProductsState}
            mmsProducts={mmsProductResults}
            numCol={underThreeProducts ? storeProductResults?.length : 3}
          />
        </div>
      </Container>
    </div>
  );
};

export default StoreProductsListing;
